import { RouteComponentProps } from "@reach/router"
import { SortOrder, toSortOrder } from "@shared/platform-api/requests"
import { DocumentTitleOverride, usePaginatedApi } from "@shared/primitive-components"
import { useQueryStringWithStateHook } from "@shared/utils/helpers/useQueryStringWithStateHooks"
import { useState } from "react"
import { unwrap } from "@shared/utils/helpers/unwrap"
import {
  SearchTripVehiclesInput,
  TripVehicle,
  TripVehicleSortKeys,
  TripVehicleSortKeysFE,
  tripVehicleSortKeysMap,
  useSearchTripVehicles,
} from "../../../api/insights"
import { ListWrapperWithContext } from "../../../components/ListWrapperWithContext"
import { usePolicyAndFleet, useAuth } from "../../../contexts"
import { buildApiInputAndGetInsightVehicles } from "../../../utils"
import { VehiclesTable } from "../../components/SafetyInsightsComponents"
import { PrevPolicyDropdown } from "../../components"

export const Vehicles: React.FC<RouteComponentProps> = () => {
  const { policy } = usePolicyAndFleet()
  const [limit, setLimit] = useQueryStringWithStateHook(`limit`)
  const [sortOrder, setSortOrder] = useQueryStringWithStateHook(`sort_order`)
  const [sortKey, setSortKey] = useQueryStringWithStateHook(`sort_key`)
  const [searchTerm, setSearchTerm] = useQueryStringWithStateHook(`term`)
  const auth = useAuth()

  const input: SearchTripVehiclesInput = {
    policyId: policy.id,
    limit: limit && /^[0-9]*$/.test(limit) ? parseInt(limit, 10) : 50,
    sortKey: (sortKey
      ? tripVehicleSortKeysMap.get(sortKey as TripVehicleSortKeysFE)
      : `currentPeriod.distanceKm`) as TripVehicleSortKeys,
    sortOrder: sortOrder ? toSortOrder(sortOrder) : (`DESC` as SortOrder),
    term: searchTerm || ``,
  }

  const queryStringAndStateSetters = {
    setLimit,
    setSortOrder,
    setSortKey,
    setSearchTerm,
  }

  // Get search trip vehicles
  const [res, reload] = useSearchTripVehicles(input)
  const { page, dataCount, sort, setSort, pagination, search } = usePaginatedApi<
    TripVehicle,
    SearchTripVehiclesInput,
    TripVehicleSortKeys,
    TripVehicleSortKeysFE
  >(input, reload, res.result, tripVehicleSortKeysMap, queryStringAndStateSetters)

  const [hasErrorInsightVehiclesCsv, setHasErrorInsightVehiclesCsv] = useState<boolean>(false)
  const [isLoadingInsightVehiclesCsv, setIsLoadingInsightVehiclesCsv] = useState<boolean>(false)

  const downloadInsightsVehiclesCsv = () => {
    setIsLoadingInsightVehiclesCsv(true)
    buildApiInputAndGetInsightVehicles(
      input,
      199,
      search.value,
      sort,
      policy.policyHolder,
      policy.policyNumber,
      async () => unwrap(await auth.getToken())
    ).then((value) => {
      setHasErrorInsightVehiclesCsv(!value)
      setIsLoadingInsightVehiclesCsv(false)
    })
  }

  return (
    <>
      <DocumentTitleOverride>Flock Motor | Safety Score - Vehicles</DocumentTitleOverride>
      <ListWrapperWithContext
        dataCount={dataCount}
        downloadFile={{
          callback: dataCount !== 0 ? downloadInsightsVehiclesCsv : undefined,
          actionTitle: isLoadingInsightVehiclesCsv ? `Loading...` : `Export Vehicle Insights`,
        }}
        pageApiStatus={{
          failed: hasErrorInsightVehiclesCsv,
          loading: res.loading,
        }}
        search={{
          ...search,
          placeholderText: `Search by VRN`,
        }}
        subTitle="All vehicles"
        subTitleTooltip="This table shows all the vehicles which have been connected in the last 60 days"
        title="Vehicles"
        listApiStatus={{
          loading: res.loading,
          failed: res.result?.tag === `err`,
        }}
        totalLabels={{
          singular: `Vehicle`,
          plural: `Vehicles`,
        }}
        PreviousPolicyDropdown={<PrevPolicyDropdown />}
      >
        <VehiclesTable
          loading={res.loading}
          page={page}
          sort={sort}
          dataCount={dataCount}
          sortData={setSort}
          pagination={pagination}
        />
      </ListWrapperWithContext>
    </>
  )
}
